import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";

let cache = null;
const subscribers = new Set();
let inflight = null;

export async function loadBranding() {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const { data } = await api.get("/public/settings");
      cache = data || {};
    } catch {
      cache = {};
    }
    subscribers.forEach((cb) => cb(cache));
    inflight = null;
    return cache;
  })();
  return inflight;
}

export function refreshBranding() {
  cache = null;
  inflight = null;
  return loadBranding();
}

export function useBranding() {
  const [b, setB] = useState(cache);
  useEffect(() => {
    subscribers.add(setB);
    if (cache) setB(cache);
    else loadBranding();
    return () => subscribers.delete(setB);
  }, []);

  const s = b || {};
  const logoUrl = s.logo_path ? fileUrl(s.logo_path) : null;
  return {
    boatName: s.boat_name || "",
    spaName: s.spa_name || "SLX-one",
    logoUrl,
    // Dedicated login-screen logo — falls back to the main logo when the
    // operator hasn't uploaded a distinct one.
    loginLogoUrl: s.login_logo_path ? fileUrl(s.login_logo_path) : logoUrl,
    // Dedicated Crew Portal logo — surfaces on the `/crew` mobile view
    // so a yacht can carry a stripped-back, staff-only brand mark
    // separate from the guest spa logo. Falls back to `logo_path` when
    // no distinct upload exists so existing installs stay branded.
    crewLogoUrl: s.crew_logo_path ? fileUrl(s.crew_logo_path) : logoUrl,
    // Dedicated admin-dashboard sidebar logo — the desktop management UI
    // gets its own mark, separate from the guest spa header, so the
    // operator can brand the backstage view distinctly. Falls back to
    // `logo_path` when unset.
    adminLogoUrl: s.admin_logo_path ? fileUrl(s.admin_logo_path) : logoUrl,
    // Dev advisory banner — polite "we're still shipping, please share
    // feedback" notice controlled from Settings. Off by default so
    // existing installs stay clean.
    devBannerEnabled: !!s.dev_banner_enabled,
    devBannerText: s.dev_banner_text || "",
    // Developer-controlled map switches — default ON so a missing settings
    // doc doesn't accidentally strip the maps out of a working install.
    journeyPortMapEnabled: s.journey_port_map_enabled !== false,
    journeyRouteMapEnabled: s.journey_route_map_enabled !== false,
    // Dedicated in-app logo shown when a guest cabin iPad is signed in
    // (kiosk mode). Falls back to `pwa_icon_path` → `logo_path` so cabins
    // without a distinct upload keep their existing header branding.
    ipadLogoUrl: s.ipad_icon_path
      ? fileUrl(s.ipad_icon_path)
      : (s.pwa_icon_path ? fileUrl(s.pwa_icon_path) : logoUrl),
    heroUrl: s.hero_image_path ? fileUrl(s.hero_image_path) : null,
    loaded: !!b,
  };
}
